import { ListingType, PreservedListingOptions, SgwForm, SHOPGOODWILL_CHANNEL_ID } from './types'
import { formatMoneyForInput } from '../../utils'
import { CategoryListingConfiguration } from '../../types/api'

interface Action extends Record<string, any> {
  type: string
}

const applyPreservedListingOptions = <T extends Record<string, unknown>>(
  form: T,
  preservedListingOptions?: PreservedListingOptions
): T => {
  if (!preservedListingOptions) {
    return form
  }

  const preservedValues: PreservedListingOptions = {}

  if (preservedListingOptions.noCombineShipping !== undefined) {
    preservedValues.noCombineShipping = preservedListingOptions.noCombineShipping
  }

  if (preservedListingOptions.itemFeatured !== undefined) {
    preservedValues.itemFeatured = preservedListingOptions.itemFeatured
  }

  if (Object.keys(preservedValues).length === 0) {
    return form
  }

  return {
    ...form,
    ...preservedValues
  }
}

export const sgwFormReducer = (sgwForm, action: Action): SgwForm => {
  switch (action.type) {
    case 'clearForm': {
      return applyPreservedListingOptions({}, action.preservedListingOptions)
    }
    case 'clearStrategyForm': {
      const { noCombineShipping, itemFeatured } = sgwForm

      return applyPreservedListingOptions(
        {
          oldCategoryConfig: sgwForm.oldCategoryConfig,
          ...sgwForm.oldCategoryConfig
        },
        { noCombineShipping, itemFeatured }
      )
    }
    case 'onValueChange': {
      const { newValues } = action

      return {
        ...sgwForm,
        ...newValues
      }
    }
    case 'categoryConfigLoaded': {
      // This is where admin market settings are applied
      const { cmcId } = action

      // types are good
      // eslint-disable-next-line prefer-destructuring
      const resData: CategoryListingConfiguration = action.resData

      const categoryConfig = {
        // We could grab default carrier from props.current.organization.settings.carrierId or API::V5::ListingConfigurationController.
        // We're using the latter since it already decides where the default carrier comes from. But either could work honestly.
        carrierId: resData.defaultCarrierId || 2, // Fedex is a good default choice
        // Apply market default settings before we apply the listing strategy configuration,
        // so that the listing strategy values will take preference.
        bidIncrement: resData.marketSettings?.shopgoodwill?.defaultBidIncrement || '1.0',
        price: formatMoneyForInput(resData.marketSettings?.shopgoodwill?.defaultStartingPrice) || '',
        shippingPrice: formatMoneyForInput(resData.marketSettings?.shopgoodwill?.defaultShippingPrice),
        reservePrice: '0.0', // admin channel settings don't allow configuring this
        handlingPrice: resData.marketSettings?.shopgoodwill?.defaultHandlingPrice || '0.0',
        categoryMarketCategoryId: cmcId,
        auctionDuration: String(resData.marketSettings?.shopgoodwill?.defaultAuctionDuration || 7),
        noCombineShipping: resData.marketSettings?.shopgoodwill?.defaultNoCombineShipping,
        itemFeatured: false
        // auctionStart is not handled. Literally nobody configures this to a non-zero value. Schema has a default of zero.
        // Settings::Markets::Shopgoodwill.where(owner: Organization.where("name ilike '%goodwill%'")).pluck(:default_auction_start).tally
      }

      return applyPreservedListingOptions(
        {
          ...sgwForm,

          // When we dispatch clearStrategyForm, we want to remove whatever values the strategy applied BUT keep the category defaults.
          // Storing this allows us to reset the form state (keeping the category/admin setting defaults)
          oldCategoryConfig: categoryConfig, // don't serialize this

          ...categoryConfig
        },
        action.preservedListingOptions
      )
    }
    case 'strategyConfigLoaded': {
      const { resData } = action

      const strategyConfiguration = resData.configuration
      const channelIds = strategyConfiguration.map(c => c.marketId)

      const newSgwForm = { ...sgwForm }

      if (channelIds.includes(SHOPGOODWILL_CHANNEL_ID)) {
        const sgwConfiguration = strategyConfiguration.find(c => c.marketId === SHOPGOODWILL_CHANNEL_ID)
        // Concurrent Shopgoodwill listings must be buy-it-now (see ListingStrategy#validate_concurrent_steps!).
        const isConcurrent = sgwConfiguration.stepType === 'CONCURRENT'

        let sgwListingType: ListingType = 'AUCTION'

        // null means "don't show this field"
        // If strategy defines buy_now_price, show it in the details drawer. Otherwise, nothing in UI.
        let sgwBuyItNowPrice = null

        // stepRank may be non-zero: with eBay + Shopgoodwill strategies, the backend returns each market's
        // first (lowest-rank) configuration, so Shopgoodwill can arrive at a later step than eBay. We still
        // populate the Shopgoodwill card from that configuration regardless of which step it lives on.

        const priceIsSet = sgwConfiguration.priceChangeBy === 'SET' && !!sgwConfiguration.priceChange
        const buyItNowIsSet = sgwConfiguration.buyItNowPriceChangeBy === 'SET' && !!sgwConfiguration.buyItNowPriceChange

        if (priceIsSet) {
          // We are able to determine the starting price.
          // Possibly override the admin configuration applied earlier.
          newSgwForm.price = formatMoneyForInput(sgwConfiguration.priceChange)
        }

        if (buyItNowIsSet) {
          const buyNowPrice = formatMoneyForInput(sgwConfiguration.buyItNowPriceChange)

          if (isConcurrent || !priceIsSet) {
            // Concurrent listings (and any buy-it-now configured without a starting bid) treat the buy-it-now
            // price as the starting price, mirroring the legacy ListingDefaultResolver#getBlankShopgoodwillListing.
            newSgwForm.price = buyNowPrice
            sgwListingType = 'FIXED_PRICE'
          } else if (sgwConfiguration.priceChange === sgwConfiguration.buyItNowPriceChange) {
            // Our SGW model conveys fixed price like this.
            // The listing strategy edit page requires users to fill it out this way (similar to the classic listing flow).
            sgwListingType = 'FIXED_PRICE'
          } else {
            // We will show them the "buy it now" price (which is higher than the starting bid) in the details drawer
            sgwBuyItNowPrice = buyNowPrice
          }
        }

        if (sgwConfiguration.shippingPriceChangeBy === 'SET' && sgwConfiguration.shippingPriceChange) {
          newSgwForm.shippingPrice = formatMoneyForInput(sgwConfiguration.shippingPriceChange)
        }

        if (sgwConfiguration.reservePriceChangeBy === 'SET' && sgwConfiguration.reservePriceChange) {
          newSgwForm.reservePrice = sgwConfiguration.reservePriceChange
        }

        if (sgwConfiguration.handlingPriceChangeBy === 'SET' && sgwConfiguration.handlingPriceChange) {
          newSgwForm.handlingPrice = sgwConfiguration.handlingPriceChange
        }

        if (sgwConfiguration.listingDuration) {
          newSgwForm.auctionDuration = sgwConfiguration.listingDuration
        }

        newSgwForm.listingType = sgwListingType
        newSgwForm.buyItNowPrice = sgwBuyItNowPrice
      }

      return newSgwForm
    }
    default: {
      throw Error(`Unknown action: ${action.type}`)
    }
  }
}
